<template>
  <div class="keyword-filter" :style="wrapperStyle">
    <div class="keyword-filter__type-wrap" :style="typeWrapStyle">
      <el-select v-model="internalType"
                 class="keyword-filter__type"
                 :placeholder="typePlaceholderText"
                 :clearable="false">
        <template #prefix>
          <span class="keyword-filter__type-mask">{{ currentTypeText }}</span>
        </template>
        <el-option v-for="option in options" :key="option.value !== undefined ? option.value : option.text"
                   :label="option.text"
                   :value="option.value">
        </el-option>
      </el-select>
    </div>
    <div class="keyword-filter__field" :style="fieldStyle">
      <el-input v-if="currentOptionType !== 'select'"
                class="keyword-filter__input"
                prefix-icon="Search"
                v-model.trim="internalValue"
                :placeholder="fieldPlaceholderText"
                clearable
                :maxlength="resolvedOption.max || 200"
                @keyup.enter.native="emitEnter">
      </el-input>
      <el-select v-else
                 class="keyword-filter__select"
                 v-model="internalValue"
                 :placeholder="fieldPlaceholderText"
                 :filterable="resolvedOption.filterable !== false"
                 :clearable="fieldClearable"
                 :multiple="resolvedOption.multiple === true"
                 :disabled="resolvedOption.disabled === true"
                 @clear="emitClear">
        <el-option v-for="(source, index) in sourceList" :key="sourceKey(source, index)"
                   :label="formatLabel(source)"
                   :value="formatValue(source)">
        </el-option>
      </el-select>
    </div>
  </div>
</template>

<script>
export default {
  name: "keywordFilter",
  props: {
    modelValue: {
      type: [String, Number, Boolean],
      default: "",
    },
    typeValue: {
      type: [String, Number, Boolean],
      default: null,
    },
    options: {
      type: Array,
      default: function () {
        return [];
      },
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    typePlaceholder: {
      type: String,
      default: "请选择",
    },
    inputPlaceholder: {
      type: String,
      default: "请输入",
    },
    width: {
      type: [String, Number],
      default: 280,
    },
    selectWidth: {
      type: [String, Number],
      default: "auto",
    },
    selectMinWidth: {
      type: [String, Number],
      default: 120,
    },
    selectMaxWidth: {
      type: [String, Number],
      default: null,
    },
    inputMinWidth: {
      type: [String, Number],
      default: 160,
    },
  },
  emits: ["update:modelValue", "update:typeValue", "enter", "type-change", "value-change", "clear"],
  data() {
    return {
      internalValue: this.modelValue === undefined ? "" : this.modelValue,
      internalType: this.typeValue != null ? this.typeValue : null,
    }
  },
  computed: {
    resolvedOption() {
      var list = this.options || [];
      var found = list.find(option => option.value === this.internalType);
      return found || list[0] || {};
    },
    fieldClearable() {
      if (this.resolvedOption && this.resolvedOption.clearable !== undefined) {
        return this.resolvedOption.clearable === true;
      }
      return this.clearable === true;
    },
    currentTypeText() {
      if (this.resolvedOption && this.resolvedOption.text) {
        return this.resolvedOption.text;
      }
      return this.typePlaceholderText;
    },
    currentOptionType() {
      return this.resolvedOption.inputType || "input";
    },
    typePlaceholderText() {
      return this.typePlaceholder || "请选择";
    },
    fieldPlaceholderText() {
      if (this.resolvedOption.placeholder) {
        return this.resolvedOption.placeholder;
      }
      return this.inputPlaceholder || "请输入";
    },
    sourceList() {
      var list = this.resolvedOption.sourceList;
      return Array.isArray(list) ? list : [];
    },
    wrapperStyle() {
      return {
        width: this.formatSize(this.width),
      };
    },
    typeWrapStyle() {
      var dynamicWidth = this.getTypeWidth();
      return {
        width: dynamicWidth,
        minWidth: this.formatSize(this.selectMinWidth),
        maxWidth: this.selectMaxWidth ? this.formatSize(this.selectMaxWidth) : "none",
        flex: "0 0 " + dynamicWidth,
      };
    },
    fieldStyle() {
      return {
        minWidth: this.shouldApplyInputMinWidth() ? this.formatSize(this.inputMinWidth) : "0px",
      };
    },
  },
  watch: {
    modelValue(val) {
      if (val !== this.internalValue) {
        this.internalValue = val;
      }
    },
    typeValue(val) {
      if (val !== this.internalType) {
        this.internalType = val;
      }
    },
    options: {
      handler(list) {
        if (!Array.isArray(list) || !list.length) {
          this.internalType = null;
          return;
        }
        var exists = list.some(function (item) {
          return item.value === this.internalType;
        }, this);
        if (!exists) {
          this.internalType = list[0].value;
        }
      },
      deep: true,
      immediate: true,
    },
    internalValue(val) {
      this.$emit("update:modelValue", val);
      this.$emit("value-change", this.getPayload());
    },
    internalType(val, oldVal) {
      if (val !== oldVal) {
        this.$emit("update:typeValue", val);
        if (oldVal !== null && oldVal !== undefined) {
          this.internalValue = "";
        }
        this.$nextTick(() => {
          this.$emit("type-change", this.getPayload());
        });
      }
    },
  },
  methods: {
    emitEnter() {
      this.$emit("enter", this.getPayload());
    },
    emitClear() {
      this.$emit("clear", this.getPayload());
    },
    getPayload() {
      return {
        value: this.internalValue,
        typeValue: this.internalType,
        option: this.resolvedOption,
        options: this.options || [],
      };
    },
    formatLabel(source) {
      if (!source) return "";
      var labelKey = (this.resolvedOption && this.resolvedOption.sourceProp && this.resolvedOption.sourceProp.label) || "text";
      return source[labelKey];
    },
    formatValue(source) {
      if (!source) return undefined;
      var valueKey = (this.resolvedOption && this.resolvedOption.sourceProp && this.resolvedOption.sourceProp.value) || "value";
      return source[valueKey];
    },
    sourceKey(source, index) {
      var value = this.formatValue(source);
      return String(value != null ? value : index);
    },
    formatSize(value) {
      if (value === undefined || value === null || value === "") {
        return "";
      }
      if (typeof value === "number") {
        return value + "px";
      }
      if (/^\d+$/.test(value)) {
        return value + "px";
      }
      return value;
    },
    getTypeWidth() {
      if (this.selectWidth && this.selectWidth !== "auto") {
        return this.formatSize(this.selectWidth);
      }
      var text = this.currentTypeText || this.typePlaceholderText || "请选择";
      var charLength = text.length;
      var width = charLength * 16 + 40;
      var min = this.parseSize(this.selectMinWidth, 0);
      var max = this.parseSize(this.selectMaxWidth, 0);
      if (width < min) width = min;
      if (max > 0 && width > max) width = max;
      return width + "px";
    },
    shouldApplyInputMinWidth() {
      return !(this.inputMinWidth === 0 || this.inputMinWidth === "0" || this.inputMinWidth === "0px");
    },
    parseSize(value, defaultValue) {
      if (typeof value === "number") {
        return value;
      }
      if (typeof value === "string") {
        var match = value.match(/\d+/);
        if (match) {
          return Number(match[0]);
        }
      }
      return defaultValue;
    },
  },
}
</script>

<style scoped>
.keyword-filter {
  display: inline-flex;
  align-items: center;
  height: 32px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  box-sizing: border-box;
}

.keyword-filter__type-wrap {
  height: 100%;
  border-right: 1px solid #dcdfe6;
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.keyword-filter__field {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: center;
}

.keyword-filter__type,
.keyword-filter__input,
.keyword-filter__select {
  width: 100%;
  height: 100%;
}

:deep(.keyword-filter__type .el-select),
:deep(.keyword-filter__select .el-select),
:deep(.keyword-filter__input .el-input) {
  width: 100%;
  height: 100%;
}

:deep(.keyword-filter__type .el-select__wrapper),
:deep(.keyword-filter__select .el-select__wrapper),
:deep(.keyword-filter__input .el-input__wrapper) {
  min-height: 30px !important;
  height: 30px !important;
  display: flex !important;
  align-items: center !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  border: none !important;
  background: transparent !important;
  box-sizing: border-box;
}

:deep(.keyword-filter__type .el-select__wrapper) {
  padding-left: 10px;
  padding-right: 8px;
  gap: 4px;
}

:deep(.keyword-filter__input .el-input__wrapper),
:deep(.keyword-filter__select .el-select__wrapper) {
  padding-left: 10px;
  padding-right: 10px;
}

:deep(.keyword-filter__type .el-select__selection),
:deep(.keyword-filter__select .el-select__selection),
:deep(.keyword-filter__input .el-input__inner) {
  display: flex;
  align-items: center;
  min-height: 20px;
  line-height: 20px;
}

:deep(.keyword-filter__type .el-select__prefix) {
  display: flex;
  align-items: center;
  color: #606266;
  margin-right: 4px;
  flex: 1;
}

.keyword-filter__type-mask {
  display: block;
  font-size: 14px;
  line-height: 20px;
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
}

:deep(.keyword-filter__input .el-input__prefix) {
  display: flex;
  align-items: center;
  margin-right: 6px;
}

:deep(.keyword-filter__type .el-select__caret),
:deep(.keyword-filter__select .el-select__caret),
:deep(.keyword-filter__input .el-input__prefix-inner) {
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.keyword-filter__type .el-select__caret) {
  margin-left: 0 !important;
  flex-shrink: 0;
}

:deep(.keyword-filter__input .el-input__inner),
:deep(.keyword-filter__type .el-select__placeholder),
:deep(.keyword-filter__select .el-select__placeholder),
:deep(.keyword-filter__type .el-select__selected-item),
:deep(.keyword-filter__select .el-select__selected-item),
:deep(.keyword-filter__type .el-select__input),
:deep(.keyword-filter__select .el-select__input) {
  font-size: 14px !important;
  color: #606266 !important;
}

:deep(.keyword-filter__type .el-select__placeholder),
:deep(.keyword-filter__select .el-select__placeholder) {
  color: #a8abb2 !important;
}

:deep(.keyword-filter__type .el-select__selected-item),
:deep(.keyword-filter__type .el-select__placeholder),
:deep(.keyword-filter__type .el-select__input) {
  opacity: 0 !important;
  width: 0 !important;
  min-width: 0 !important;
}

:deep(.keyword-filter__type .el-select__selection),
:deep(.keyword-filter__select .el-select__selection) {
  min-width: 0;
}
</style>
